import { format } from 'date-fns';
import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import {
  endpointOptions,
  environmentOptions,
  selectEndpoint,
  selectEnvironment,
} from '../apiExplorerSlice';

/**
 * Generates a formatted export filename based on the current API Explorer environment, endpoint, and date.
 *
 * This hook retrieves the current environment and endpoint values from the application state, maps these values
 * to their corresponding labels using predefined options, and constructs a filename that includes the formatted
 * current date. The resulting filename follows the pattern: "API_[EnvironmentLabel]_[EndpointLabel]_[MM-dd-yyyy]".
 *
 * @returns {string} A formatted API export filename.
 *
 * @example
 * const filename = useGetAPIExplorerExportFilename();
 * // filename could be "API_Production_UserEndpoints_04-27-2023"
 */
const useGetAPIExplorerExportFilename = () => {
  const environment = useAppSelector(selectEnvironment);
  const endpoint = useAppSelector(selectEndpoint);

  const currEnvironment = useMemo(() => {
    return environmentOptions.find((option) => option.value === environment)
      ?.label;
  }, [environment]);

  const currEndpoint = useMemo(() => {
    return endpointOptions.find((option) => option.value === endpoint)?.label;
  }, [endpoint]);

  const filename = useMemo(() => {
    const formattedDate = format(new Date(), 'MM-dd-yyyy');
    return `API_${currEnvironment}_${currEndpoint}_${formattedDate}`;
  }, [currEnvironment, currEndpoint]);

  return filename;
};

export default useGetAPIExplorerExportFilename;
